import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ChangePasswordData } from '@/types/index';
import { Input } from '@components/common/Input';
import { Button } from '@components/common/Button';
import { authService } from '@services/auth-service';

interface ChangePasswordFormProps {
  onSuccess: () => void;
}

export const ChangePasswordForm: FC<ChangePasswordFormProps> = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ChangePasswordData>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: ChangePasswordData) => {
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);

      await authService.changePassword(data);

      setSuccess('Mot de passe changé avec succès');
      reset();
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Erreur lors du changement de mot de passe:', err);
      if (err.response?.status === 401) {
        setError('Le mot de passe actuel est incorrect');
      } else {
        setError('Erreur lors du changement de mot de passe. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          {success}
        </div>
      )}

      <Input
        label="Mot de passe actuel"
        type="password"
        {...register('currentPassword', {
          required: 'Le mot de passe actuel est requis',
        })}
        error={errors.currentPassword?.message}
        disabled={isSubmitting}
      />

      <Input
        label="Nouveau mot de passe"
        type="password"
        {...register('newPassword', {
          required: 'Le nouveau mot de passe est requis',
          minLength: {
            value: 8,
            message: 'Le mot de passe doit contenir au moins 8 caractères',
          },
          validate: (value) => {
            const currentPassword = watch('currentPassword');
            if (value === currentPassword) {
              return 'Le nouveau mot de passe doit être différent de l\'actuel';
            }
            return true;
          },
        })}
        error={errors.newPassword?.message}
        disabled={isSubmitting}
      />

      <Input
        label="Confirmer le nouveau mot de passe"
        type="password"
        {...register('confirmPassword', {
          required: 'Veuillez confirmer le nouveau mot de passe',
          validate: (value) => {
            if (value !== newPassword) {
              return 'Les mots de passe ne correspondent pas';
            }
            return true;
          },
        })}
        error={errors.confirmPassword?.message}
        disabled={isSubmitting}
      />

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="primary" disabled={isSubmitting}>
          {isSubmitting ? 'Changement en cours...' : 'Changer le mot de passe'}
        </Button>
      </div>
    </form>
  );
};
